import { createContext, useContext, useEffect } from "react";

import { noop } from "@ryde/shared";

import type {
  IMapLibrePlaceInfoContext,
  TMapLibreEditingTool,
} from "./maplibre-place-info.models";

/**
 * Place info's state on one map, published by `MapLibreMapHost` to every layer below it. Outside a host
 * there is no place info: nothing is picking, and there is nothing to end.
 */
export const MapLibrePlaceInfoContext =
  createContext<IMapLibrePlaceInfoContext>({
    isPickingPlace: false,
    endPlaceInfo: noop,
  });

/**
 * Whether place info owns the map's pointer right now. An editing tool reads it and stands down — its
 * listeners off, its work kept — so a click meant to look a place up cannot also move a corner or finish
 * a lasso. Outside a host it reads false, so a tool never pauses for a mode its map does not have.
 */
export const useMapLibreIsPickingPlace = (): boolean =>
  useContext(MapLibrePlaceInfoContext).isPickingPlace;

/**
 * Picking an editing tool turns place info off, so the tool works at once instead of sitting paused, which
 * looks like a stuck map. Place info turned on over a tool still pauses it until place info is off again.
 */
export const useMapLibreEndPlaceInfoOnToolPick = (
  activeTool: TMapLibreEditingTool | undefined,
): void => {
  const { endPlaceInfo } = useContext(MapLibrePlaceInfoContext);

  useEffect(() => {
    if (activeTool) {
      endPlaceInfo();
    }
  }, [activeTool, endPlaceInfo]);
};
